import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "../components/Navbar";
import BackButton from "../components/BackButton";
import BottomNav from "../components/BottomNav";
import SEO from "../components/SEO";
import api from "../lib/api";
import { MODULES, readProgress, pullServerProgress } from "../lib/academyContent";
import { useAuth } from "../lib/auth";
import {
  GraduationCap, PlayCircle, BookOpen, TrendingUp, Clock, Check, ArrowRight, Sparkles,
} from "lucide-react";

const ICONS = { TrendingUp, BookOpen, PlayCircle };

function ModuleCard({ mod }) {
  const [progress, setProgress] = useState({ completed: [], updatedAt: null });

  useEffect(() => {
    setProgress(readProgress(mod.id));
    // Refresh whenever the tab/window regains focus (e.g. user returns from the module)
    const onFocus = () => setProgress(readProgress(mod.id));
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [mod.id]);

  const Icon = ICONS[mod.icon] || BookOpen;
  const total = mod.steps.length;
  const done = progress.completed.length;
  const pct = Math.round((done / total) * 100);
  const state = done === 0 ? "start" : done >= total ? "done" : "continue";

  return (
    <Link
      to={`/academy/${mod.id}`}
      className="group text-left glass rounded-2xl p-5 hover:border-nexus-pink/40 transition-all block relative overflow-hidden"
      data-testid={`academy-module-${mod.id}`}
    >
      {/* Accent halo */}
      <div className={`absolute -top-10 -right-10 w-32 h-32 rounded-full bg-gradient-to-br ${mod.accent} opacity-10 group-hover:opacity-25 transition-opacity blur-2xl`} />

      <div className="flex items-center justify-between mb-3 relative">
        <div className={`h-11 w-11 rounded-xl flex items-center justify-center bg-gradient-to-br ${mod.accent}`}>
          <Icon className="h-5 w-5 text-white" />
        </div>
        <div className="flex items-center gap-1.5">
          <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-white/5 border border-white/10 ${mod.tone}`}>{mod.tag}</span>
          {state === "done" && (
            <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-emerald-500/15 border border-emerald-500/25 text-emerald-300 flex items-center gap-1">
              <Check className="h-2.5 w-2.5" /> Done
            </span>
          )}
        </div>
      </div>

      <div className="font-display font-bold text-lg leading-snug mb-2 relative">{mod.title}</div>
      <p className="text-[13px] text-zinc-400 leading-relaxed line-clamp-2 mb-3 min-h-[36px]">{mod.summary}</p>

      <div className="flex items-center gap-3 text-[11px] text-zinc-500 uppercase tracking-widest mb-4">
        <span className="flex items-center gap-1"><Clock className="h-3 w-3" /> {mod.duration} min</span>
        <span className="flex items-center gap-1"><BookOpen className="h-3 w-3" /> {total} steps</span>
      </div>

      {state !== "start" && (
        <div className="mb-3">
          <div className="h-1 bg-white/5 rounded-full overflow-hidden">
            <div className={`h-full bg-gradient-to-r ${mod.accent} transition-all duration-500`} style={{ width: `${pct}%` }} />
          </div>
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 mt-1 tabular-nums">{done} / {total} · {pct}%</div>
        </div>
      )}

      <div className="w-full h-10 rounded-full nexus-gradient text-white text-sm font-semibold flex items-center justify-center gap-2 group-hover:brightness-110 transition-all" data-testid={`academy-open-${mod.id}`}>
        {state === "start" && <><PlayCircle className="h-4 w-4" /> Start module</>}
        {state === "continue" && <><ArrowRight className="h-4 w-4" /> Continue where you left off</>}
        {state === "done" && <><Sparkles className="h-4 w-4" /> Revisit module</>}
      </div>
    </Link>
  );
}

export default function Academy() {
  const { user } = useAuth();
  const [tagFilter, setTagFilter] = useState("All");
  const [hydrated, setHydrated] = useState(0); // bump to force ModuleCards to re-read localStorage
  const TAGS = ["All", ...Array.from(new Set(MODULES.map((m) => m.tag)))];

  const filtered = tagFilter === "All" ? MODULES : MODULES.filter((m) => m.tag === tagFilter);

  // Overall progress across all modules
  const totalSteps = MODULES.reduce((s, m) => s + m.steps.length, 0);
  const [doneSteps, setDoneSteps] = useState(0);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      // Pull cross-device progress once on mount, then compute the totals
      if (user) {
        await pullServerProgress(api);
      }
      if (cancelled) return;
      const total = MODULES.reduce((s, m) => s + readProgress(m.id).completed.length, 0);
      setDoneSteps(total);
      setHydrated((n) => n + 1);
    })();
    return () => { cancelled = true; };
  }, [user]);
  const overallPct = Math.round((doneSteps / totalSteps) * 100);

  return (
    <div className="min-h-screen grain">
      <Navbar />
      <SEO title="Academy · Nexus" description="Short, sharp tutorials on growth, pricing, editing and running your creator business." path="/academy" />
      <div className="max-w-5xl mx-auto px-4 md:px-6 lg:px-10 pt-6">
        <BackButton fallback="/creator" label="Back" />
      </div>
      <div className="max-w-5xl mx-auto px-4 md:px-6 lg:px-10 pt-4 pb-24 space-y-8">
        <div>
          <div className="text-xs uppercase tracking-[0.3em] text-zinc-500 mb-2 flex items-center gap-2">
            <GraduationCap className="h-4 w-4" /> Academy
          </div>
          <h1 className="font-display font-bold text-4xl md:text-5xl tracking-tighter">
            Level up your <span className="nexus-gradient-text">creator craft.</span>
          </h1>
          <p className="text-zinc-400 mt-2 max-w-2xl">
            Short, sharp tutorials on growth, pricing, editing, and running your creator business like a pro. Every module is step-by-step — mark them complete as you go and pick up where you left off.
          </p>
          <div className="mt-4 flex items-center gap-3 flex-wrap">
            <span className="inline-block text-[10px] uppercase tracking-widest px-2 py-1 rounded-full border border-yellow-500/30 bg-yellow-500/10 text-yellow-300">
              Beta · New modules weekly
            </span>
            {doneSteps > 0 && (
              <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-zinc-500" data-testid="academy-overall-progress">
                <span>Overall progress:</span>
                <div className="h-1 w-24 bg-white/5 rounded-full overflow-hidden">
                  <div className="h-full nexus-gradient" style={{ width: `${overallPct}%` }} />
                </div>
                <span className="text-white tabular-nums font-mono">{doneSteps}/{totalSteps} · {overallPct}%</span>
              </div>
            )}
          </div>
        </div>

        {/* Tag filter */}
        <div className="flex gap-1.5 overflow-x-auto no-scrollbar" data-testid="academy-tag-filter">
          {TAGS.map((tag) => {
            const active = tagFilter === tag;
            return (
              <button
                key={tag}
                onClick={() => setTagFilter(tag)}
                className={`shrink-0 h-8 px-4 rounded-full text-[11px] uppercase tracking-widest font-semibold transition-all ${
                  active ? "nexus-gradient text-white" : "bg-white/5 text-zinc-400 border border-white/10 hover:text-white"
                }`}
                data-testid={`academy-tag-${tag.toLowerCase()}`}
              >
                {tag}
              </button>
            );
          })}
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="academy-modules">
          {filtered.map((m) => (
            <ModuleCard key={`${m.id}-${hydrated}`} mod={m} />
          ))}
        </div>
      </div>
      <BottomNav />
    </div>
  );
}
